import type {
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
} from '../types/domain';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import { parseMemoFieldStorageDeep } from './programCellDisplayText';
import {
  mergeProgramLearningContentFields,
  parseProgramLearningContentFieldsFromEditor,
  readProgramLearningContentFields,
  serializeProgramLearningContentFieldsForEditor,
  type ProgramLearningContentRowLike,
} from './programLearningContentFields';
import type { ProgramProgressRow } from './programProgressRows';

export function isPersistableUuid(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id.trim());
}

export function resolvePresentationRefIdFromLearningText(learningText: string | null | undefined): string {
  const refId = parseProgramLearningContentFieldsFromEditor(learningText).presentationRefId?.trim() ?? '';
  return refId && isPersistableUuid(refId) ? refId : '';
}

export function resolvePresentationRefIdFromLearningContent(
  source: string | ProgramLearningContentRowLike | null | undefined
): string {
  if (source == null) return '';
  const fields =
    typeof source === 'string'
      ? parseProgramLearningContentFieldsFromEditor(source)
      : readProgramLearningContentFields(source);
  const refId = fields.presentationRefId?.trim() ?? '';
  return refId && isPersistableUuid(refId) ? refId : '';
}

function resolveLegacyProgramContentSlideOwner(
  mode: 'default' | 'fixed_to_unit' | 'project_to_template' | undefined,
  row: ProgramProgressRow
): ProgramContentSlideOwnerRef | null {
  const m = mode ?? 'default';
  const fixedId = (row.fixedContentId ?? '').trim();

  if (m === 'fixed_to_unit') {
    if (fixedId && isPersistableUuid(fixedId)) return { kind: 'fixed_row', id: fixedId };
    return null;
  }

  if (fixedId && isPersistableUuid(fixedId)) return { kind: 'scoped_row', id: fixedId };
  return null;
}

/** 편제·프로는 programCellsByAnnualKey.id가 scoped_row id — program_cell로 취급하지 않는다 */
function resolveCellBackedPersistableSlideOwner(
  mode: 'default' | 'fixed_to_unit' | 'project_to_template' | undefined,
  row: ProgramProgressRow,
  cell: ProgramProgressCell | undefined
): ProgramContentSlideOwnerRef | null {
  const legacy = resolveLegacyProgramContentSlideOwner(mode, row);
  if (legacy) return legacy;
  const m = mode ?? 'default';
  const cid = (cell?.id ?? '').trim();
  if (!cid || !isPersistableUuid(cid)) return null;
  if (m === 'fixed_to_unit') return { kind: 'fixed_row', id: cid };
  const fixedId = (row.fixedContentId ?? '').trim();
  if (fixedId && isPersistableUuid(fixedId)) return null;
  return { kind: 'program_cell', id: cid };
}

function pushCellBackedPersistableSlideOwner(
  owners: ProgramContentSlideOwnerRef[],
  mode: 'default' | 'fixed_to_unit' | 'project_to_template' | undefined,
  row: ProgramProgressRow,
  cell: ProgramProgressCell | undefined
): void {
  const owner = resolveCellBackedPersistableSlideOwner(mode, row, cell);
  if (!owner) return;
  if (!owners.some((o) => o.kind === owner.kind && o.id === owner.id)) owners.push(owner);
}

/**
 * 프로그램 표에서 Shift+U 시 DB에 슬라이드 캔버스를 묶을 owner.
 * - 신규: 학습내용 JSON의 `presentationRefId`
 * - 고정 모드 fallback: `fixed_row`
 * - 편제/프로젝트 fallback: `scoped_row`
 */
export function resolveProgramContentSlideOwner(
  mode: 'default' | 'fixed_to_unit' | 'project_to_template' | undefined,
  row: ProgramProgressRow,
  cell: ProgramProgressCell | undefined,
  learningText?: string | null
): ProgramContentSlideOwnerRef | null {
  const presentationRefId = resolvePresentationRefIdFromLearningText(learningText ?? '');
  if (presentationRefId) return { kind: 'presentation_ref', id: presentationRefId };
  return resolveCellBackedPersistableSlideOwner(mode, row, cell);
}

export function resolveProgramPresentationLearningTextForIndicator(
  ...sources: Array<string | ProgramLearningContentRowLike | null | undefined>
): string {
  const fields = mergeProgramLearningContentFields(...sources);
  return serializeProgramLearningContentFieldsForEditor(fields);
}

export function mergeProgramContentSlideOwnerCandidates(
  ...lists: ProgramContentSlideOwnerRef[][]
): ProgramContentSlideOwnerRef[] {
  const out: ProgramContentSlideOwnerRef[] = [];
  for (const list of lists) {
    for (const owner of list) {
      const id = owner.id?.trim();
      if (!id) continue;
      if (!out.some((o) => o.kind === owner.kind && o.id === id)) out.push(owner);
    }
  }
  return out;
}

export function resolveProgramContentSlideOwnerCandidatesForIndicator(params: {
  mode: 'default' | 'fixed_to_unit' | 'project_to_template' | undefined;
  row: ProgramProgressRow;
  cell: ProgramProgressCell | undefined;
  learningText: string;
  syncMirrorIndicatorCell?: ProgramProgressCell;
}): ProgramContentSlideOwnerRef[] {
  const primary = resolveProgramContentSlideOwnerCandidates(
    params.mode,
    params.row,
    params.cell,
    params.learningText
  );
  const repCell = params.syncMirrorIndicatorCell;
  if (!repCell?.id?.trim()) return primary;
  const repRow: ProgramProgressRow = { ...params.row, fixedContentId: repCell.id };
  const repCandidates = resolveProgramContentSlideOwnerCandidates(
    params.mode,
    repRow,
    repCell,
    repCell.learning_text ?? ''
  );
  return mergeProgramContentSlideOwnerCandidates(primary, repCandidates);
}

/** 프로그램 표에서 표시/조회용 canonical owner 목록 */
export function resolveProgramContentSlideOwnerCandidates(
  mode: 'default' | 'fixed_to_unit' | 'project_to_template' | undefined,
  row: ProgramProgressRow,
  cell: ProgramProgressCell | undefined,
  learningText?: string | null
): ProgramContentSlideOwnerRef[] {
  const owners: ProgramContentSlideOwnerRef[] = [];
  const presentationRefId = resolvePresentationRefIdFromLearningText(learningText ?? '');
  if (presentationRefId) {
    pushCellBackedPersistableSlideOwner(owners, mode, row, cell);
    const legacy = resolveLegacyProgramContentSlideOwner(mode, row);
    if (legacy && !owners.some((o) => o.kind === legacy.kind && o.id === legacy.id)) owners.push(legacy);
    owners.push({ kind: 'presentation_ref', id: presentationRefId });
    return owners;
  }
  const legacy = resolveLegacyProgramContentSlideOwner(mode, row);
  if (legacy && !owners.some((owner) => owner.kind === legacy.kind && owner.id === legacy.id)) {
    owners.push(legacy);
  }
  pushCellBackedPersistableSlideOwner(owners, mode, row, cell);
  return owners;
}

/**
 * 프로그램 표 Shift+U: `presentation_ref`만 쓰면 DB와 어긋날 수 있어, 같은 슬롯의
 * `program_cell` / `scoped_row` / `fixed_row`가 있으면 그 owner로 연다.
 */
export function resolveProgramOpenSlideDbOwner(
  mode: 'default' | 'fixed_to_unit' | 'project_to_template' | undefined,
  row: ProgramProgressRow,
  cell: ProgramProgressCell | undefined,
  learningText?: string | null
): ProgramContentSlideOwnerRef | null {
  const persist: ProgramContentSlideOwnerRef[] = [];
  pushCellBackedPersistableSlideOwner(persist, mode, row, cell);
  const legacy = resolveLegacyProgramContentSlideOwner(mode, row);
  if (legacy && !persist.some((o) => o.kind === legacy.kind && o.id === legacy.id)) {
    persist.push(legacy);
  }
  const fromScoped = persist.find((o) => o.kind === 'scoped_row');
  if (fromScoped) return fromScoped;
  const fromFixed = persist.find((o) => o.kind === 'fixed_row');
  if (fromFixed) return fromFixed;
  const fromCell = persist.find((o) => o.kind === 'program_cell');
  if (fromCell) return fromCell;
  return resolveProgramContentSlideOwner(mode, row, cell, learningText);
}

/** 연간진도표 행 빌드 시와 동일한 우선순위로 슬라이드 DB owner 결정 */
function annualLearningPresentationRefId(params: {
  isFixedSlot: boolean;
  fixedRow: ProgramFixedContentRow | null;
  scopedRow: ProgramScopedContentRow | null;
  programCell: ProgramProgressCell | undefined;
}): string {
  if (params.isFixedSlot) {
    return resolvePresentationRefIdFromLearningContent(params.fixedRow);
  }
  return (
    resolvePresentationRefIdFromLearningContent(params.scopedRow) ||
    resolvePresentationRefIdFromLearningContent(params.programCell)
  );
}

/** DB에 슬라이드가 묶이는 행(program_cell → scoped_row → fixed_row). `presentation_ref`는 제외 */
function appendPersistableAnnualSlideOwners(
  owners: ProgramContentSlideOwnerRef[],
  params: {
    isFixedSlot: boolean;
    fixedRow: ProgramFixedContentRow | null;
    scopedRow: ProgramScopedContentRow | null;
    programCell: ProgramProgressCell | undefined;
  }
): void {
  if (params.isFixedSlot) {
    const fid = (params.fixedRow?.id ?? '').trim();
    if (fid && isPersistableUuid(fid)) {
      const o: ProgramContentSlideOwnerRef = { kind: 'fixed_row', id: fid };
      if (!owners.some((x) => x.kind === o.kind && x.id === o.id)) owners.push(o);
    }
    return;
  }
  const cid = (params.programCell?.id ?? '').trim();
  if (cid && isPersistableUuid(cid)) {
    const o: ProgramContentSlideOwnerRef = { kind: 'program_cell', id: cid };
    if (!owners.some((x) => x.kind === o.kind && x.id === o.id)) owners.push(o);
  }
  const sid = (params.scopedRow?.id ?? '').trim();
  if (sid && isPersistableUuid(sid)) {
    const o: ProgramContentSlideOwnerRef = { kind: 'scoped_row', id: sid };
    if (!owners.some((x) => x.kind === o.kind && x.id === o.id)) owners.push(o);
  }
}

/**
 * 연간진도표 Shift+U: 슬라이드 JSON에 `presentation_ref`만 있어도 실제 페이지는
 * `program_progress_cells` / `program_scoped_content_rows` / `program_fixed_content_rows`에 묶인 경우가 많음.
 * 해당 원본 owner로 열어야 DB 조회·저장이 동작한다.
 */
export function resolveAnnualSlideOpenSlideDbOwner(params: {
  isFixedSlot: boolean;
  fixedRow: ProgramFixedContentRow | null;
  scopedRow: ProgramScopedContentRow | null;
  programCell: ProgramProgressCell | undefined;
}): ProgramContentSlideOwnerRef | null {
  const persist: ProgramContentSlideOwnerRef[] = [];
  appendPersistableAnnualSlideOwners(persist, params);
  /** 프로그램 표(편제·프로)는 scoped_row learning_text·presence를 기준으로 한다. 레거시 program_cell보다 우선 */
  const fromScoped = persist.find((o) => o.kind === 'scoped_row');
  if (fromScoped) return fromScoped;
  const fromFixed = persist.find((o) => o.kind === 'fixed_row');
  if (fromFixed) return fromFixed;
  const fromCell = persist.find((o) => o.kind === 'program_cell');
  if (fromCell) return fromCell;
  return resolveAnnualSlideContentSlideOwner(params);
}

/**
 * `ProgramFixedPage` / `ProgramTrackPage` / `ProgramProjectPage`의 `workspaceScopeId`와 동일 규칙.
 * 연간진도표에서 슬라이드 탭 저장 시 `ProgramContentSlidePayload.scopeId`에 넣는다.
 */
export function buildAnnualSlideWorkspaceScopeId(params: {
  programKindLabel: string;
  isFixedSlot: boolean;
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  projectId: string | null | undefined;
  /** ProgramTrackPage `workspaceScopeKey` (`편제명|시간표텍스트` 등) */
  trackWorkspaceScopeKey: string;
}): string {
  const sid = params.workspaceId.trim();
  const gid = params.gradeId.trim();
  if (!sid || !gid) return '';
  const y = params.academicYear;
  const t = params.termNumber;
  if (params.isFixedSlot) {
    return `fixed|${sid}|${gid}|${y}|${t}`;
  }
  if (params.programKindLabel === '프로') {
    const pid = (params.projectId ?? '').trim();
    if (!pid) return '';
    return `project|${sid}|${gid}|${y}|${t}|${pid}`;
  }
  const tk = (params.trackWorkspaceScopeKey ?? '').trim();
  if (!tk) return '';
  return `track|${sid}|${gid}|${y}|${t}|${tk}`;
}

/**
 * DB에 실제 슬라이드 페이지가 붙어 있는 owner를 고른다(삼각형 presence와 로드 owner 일치).
 * `candidates`는 연간 `slideOwnerCandidates` 또는 프로그램 표 `resolveProgramContentSlideOwnerCandidates` 결과.
 */
export function pickProgramContentSlideOwnerWithDbPresence(
  candidates: ProgramContentSlideOwnerRef[],
  presenceKeys: ReadonlySet<string>,
  presenceKeyOf: (owner: ProgramContentSlideOwnerRef) => string,
  fallbackOwner: ProgramContentSlideOwnerRef | null
): ProgramContentSlideOwnerRef | null {
  const order: ProgramContentSlideOwnerRef['kind'][] = [
    'presentation_ref',
    'scoped_row',
    'fixed_row',
    'program_cell',
  ];
  for (const kind of order) {
    const hit = candidates.find((o) => o.kind === kind && presenceKeys.has(presenceKeyOf(o)));
    if (hit) return hit;
  }
  return fallbackOwner;
}

export function resolveAnnualSlideContentSlideOwner(params: {
  isFixedSlot: boolean;
  fixedRow: ProgramFixedContentRow | null;
  scopedRow: ProgramScopedContentRow | null;
  programCell: ProgramProgressCell | undefined;
}): ProgramContentSlideOwnerRef | null {
  const { isFixedSlot, fixedRow, scopedRow, programCell } = params;
  const presentationRefId = annualLearningPresentationRefId(params);
  if (presentationRefId) return { kind: 'presentation_ref', id: presentationRefId };

  if (isFixedSlot) {
    const fid = (fixedRow?.id ?? '').trim();
    if (fid && isPersistableUuid(fid)) return { kind: 'fixed_row', id: fid };
    return null;
  }
  const sid = (scopedRow?.id ?? '').trim();
  if (sid && isPersistableUuid(sid)) return { kind: 'scoped_row', id: sid };
  const cid = (programCell?.id ?? '').trim();
  if (cid && isPersistableUuid(cid)) return { kind: 'program_cell', id: cid };
  return null;
}

/** 연간진도표 표시/조회용 canonical owner 목록 */
export function resolveAnnualSlideContentOwnerCandidates(params: {
  isFixedSlot: boolean;
  fixedRow: ProgramFixedContentRow | null;
  scopedRow: ProgramScopedContentRow | null;
  programCell: ProgramProgressCell | undefined;
}): ProgramContentSlideOwnerRef[] {
  const owners: ProgramContentSlideOwnerRef[] = [];
  const presentationRefId = annualLearningPresentationRefId(params);
  if (presentationRefId) {
    appendPersistableAnnualSlideOwners(owners, params);
    owners.push({ kind: 'presentation_ref', id: presentationRefId });
    return owners;
  }

  appendPersistableAnnualSlideOwners(owners, params);
  return owners;
}

/** DB에 ref가 없어도 프로그램 표 초안·슬라이드 탭 저장의 `learning_text`에 ref가 있으면 draft owner를 후보에 넣는다 */
export function appendPresentationRefOwnerFromLearningText(
  owners: ProgramContentSlideOwnerRef[],
  learningText: string
): ProgramContentSlideOwnerRef[] {
  const refId = resolvePresentationRefIdFromLearningText(learningText);
  if (!refId || !isPersistableUuid(refId)) return owners;
  if (owners.some((o) => o.kind === 'presentation_ref' && o.id === refId)) return owners;
  return [...owners, { kind: 'presentation_ref', id: refId }];
}
